/**
 * The formula as a model sends it: a FLAT LIST of nodes that name their inputs
 * by id, plus the id of the node holding the answer.
 *
 * The nested tree is the natural shape and the wrong one for the wire. The AI
 * SDK renders a recursive zod schema as a draft-7 `$ref` loop; Google's
 * converter cannot inline a loop, falls back to sending the raw schema, and the
 * API then refuses a loop through a required property — or, when the loop
 * points were made optional, chokes flattening it ("Schema is too complex").
 * Both were observed from this repo within the hour. A flat list keeps the wire
 * schema finite and deterministic, and the reader rebuilds the tree, proving
 * ids, references, acyclicity, reach and size as it goes.
 *
 * `compose-author.ts` proved the shape on the setup tool, where a node names a
 * field by its draft KEY because no field has an id yet. This file is the same
 * idea for every OTHER tool, where the record is already made and a node names
 * a field by its id — so the answer is a `FormulaNode` the checker and the
 * evaluator take as they are. The two readers are deliberately not merged:
 * merging them would put a key-or-id union in the middle of both.
 *
 * Every refusal is collected before the answer, never first-problem-wins, so a
 * model's repair pass sees the whole list. That is the rule `checkFormula` and
 * `links.ts` both already keep.
 */

import {
  FORMULA_MAX_DEPTH,
  FORMULA_MAX_NODES,
  isFormulaAggOp,
  isFormulaCalcOp,
  isFormulaTestOp,
  testNeedsRight,
  type FormulaAggOp,
  type FormulaCalcOp,
  type FormulaNode,
  type FormulaProblem,
  type FormulaProblemCode,
  type FormulaTestOp,
} from './formula';

/* ------------------------------------------------------------------ the wire */

/** One node, naming its inputs by id instead of holding them. */
export type FormulaWireNode =
  | { id: string; kind: 'field'; fieldId: string }
  | { id: string; kind: 'hop'; via: string; fieldId: string }
  | { id: string; kind: 'const'; value: number | string | boolean }
  | { id: string; kind: 'calc'; op: FormulaCalcOp; left: string; right: string }
  | { id: string; kind: 'round'; of: string; decimals: number }
  | { id: string; kind: 'if'; left: string; op: FormulaTestOp; right?: string; then: string; otherwise: string }
  | { id: string; kind: 'days_between'; from: string; to: string }
  | { id: string; kind: 'concat'; parts: string[] }
  | { id: string; kind: 'coalesce'; of: string[] }
  /** A leaf: `lines` and `of` are field ids, not node ids. */
  | { id: string; kind: 'agg'; op: FormulaAggOp; lines: string; of?: string };

export type FormulaWire = {
  /** The id of the node that holds the answer. */
  root: string;
  nodes: readonly FormulaWireNode[];
};

/* ---------------------------------------------------------------- the reader */

const problem = (code: FormulaProblemCode, message: string, fieldId?: string): FormulaProblem =>
  fieldId === undefined ? { code, message } : { code, message, fieldId };

/**
 * The flat list read back into a tree, or every reason it cannot be.
 *
 * The checks are the ones a flat list needs and a tree gets for free: two
 * nodes with one id, a name nothing gives, a ring, a node nothing reaches, and
 * the two caps a stored tree also keeps. A node nobody reaches is reported and
 * not quietly dropped, because it is nearly always a model's half-finished
 * edit and dropping it would answer a formula the model did not mean.
 */
export const readFormulaWire = (
  wire: FormulaWire,
): { ok: true; expr: FormulaNode } | { ok: false; problems: FormulaProblem[] } => {
  const problems: FormulaProblem[] = [];

  if (wire.nodes.length > FORMULA_MAX_NODES) {
    problems.push(
      problem(
        'FORMULA_TOO_BIG',
        `A formula holds at most ${FORMULA_MAX_NODES} pieces; this one holds ${wire.nodes.length}.`,
      ),
    );
  }

  const byId = new Map<string, FormulaWireNode>();
  for (const node of wire.nodes) {
    if (byId.has(node.id)) {
      problems.push(
        problem('FORMULA_NODE_DUPLICATE', `Two pieces are both called "${node.id}". Give each one its own name.`),
      );
      continue;
    }
    byId.set(node.id, node);
  }

  if (!byId.has(wire.root)) {
    problems.push(
      problem('FORMULA_ROOT_UNKNOWN', `The answer is said to be "${wire.root}", which is not one of the pieces.`),
    );
  }

  const reached = new Set<string>();

  /** One node, rebuilt. Null on a problem, which is recorded and walked past. */
  const build = (id: string, above: ReadonlySet<string>, depth: number): FormulaNode | null => {
    const node = byId.get(id);
    if (node === undefined) {
      problems.push(problem('FORMULA_NODE_UNKNOWN', `"${id}" is named but never given.`));
      return null;
    }
    // Named by a parent is reached, whatever happens next: a piece that is too
    // deep must not also be reported as one nobody wanted.
    reached.add(id);
    if (above.has(id)) {
      problems.push(problem('FORMULA_CYCLE', `"${id}" ends up reading itself. A formula is a tree, not a loop.`));
      return null;
    }
    if (depth > FORMULA_MAX_DEPTH) {
      problems.push(problem('FORMULA_TOO_BIG', `A formula nests at most ${FORMULA_MAX_DEPTH} deep.`));
      return null;
    }
    const seen = new Set(above).add(id);
    const kid = (childId: string): FormulaNode | null => build(childId, seen, depth + 1);

    switch (node.kind) {
      case 'field':
        return node.fieldId === '' ? shapeless(problems, id, 'names no field') : { kind: 'field', fieldId: node.fieldId };

      case 'hop':
        if (node.via === '' || node.fieldId === '') return shapeless(problems, id, 'names no field to read through');
        return { kind: 'hop', via: node.via, fieldId: node.fieldId };

      case 'const':
        if (typeof node.value === 'number' && !Number.isFinite(node.value)) {
          return shapeless(problems, id, 'holds a number nobody can write down');
        }
        return { kind: 'const', value: node.value };

      case 'calc': {
        if (!isFormulaCalcOp(node.op)) return shapeless(problems, id, `is joined by "${String(node.op)}"`);
        const left = kid(node.left);
        const right = kid(node.right);
        return left === null || right === null ? null : { kind: 'calc', op: node.op, left, right };
      }

      case 'round': {
        const of = kid(node.of);
        if (of === null) return null;
        if (typeof node.decimals !== 'number' || !Number.isFinite(node.decimals)) {
          return shapeless(problems, id, 'rounds to no number of places');
        }
        return { kind: 'round', of, decimals: Math.min(8, Math.max(0, Math.trunc(node.decimals))) };
      }

      case 'if': {
        if (!isFormulaTestOp(node.op)) return shapeless(problems, id, `asks "${String(node.op)}"`);
        const left = kid(node.left);
        const then = kid(node.then);
        const otherwise = kid(node.otherwise);
        if (!testNeedsRight(node.op)) {
          return left === null || then === null || otherwise === null
            ? null
            : { kind: 'if', left, op: node.op, then, otherwise };
        }
        if (node.right === undefined) return shapeless(problems, id, `asks "${node.op}" against nothing`);
        const right = kid(node.right);
        return left === null || right === null || then === null || otherwise === null
          ? null
          : { kind: 'if', left, op: node.op, right, then, otherwise };
      }

      case 'days_between': {
        const from = kid(node.from);
        const to = kid(node.to);
        return from === null || to === null ? null : { kind: 'days_between', from, to };
      }

      case 'concat': {
        if (!Array.isArray(node.parts) || node.parts.length === 0) {
          return shapeless(problems, id, 'joins nothing together');
        }
        const parts = node.parts.map(kid);
        return parts.some((one) => one === null) ? null : { kind: 'concat', parts: parts as FormulaNode[] };
      }

      case 'coalesce': {
        if (!Array.isArray(node.of) || node.of.length < 2) {
          return shapeless(problems, id, 'needs a second choice to fall back on');
        }
        const of = node.of.map(kid);
        return of.some((one) => one === null) ? null : { kind: 'coalesce', of: of as FormulaNode[] };
      }

      case 'agg': {
        if (!isFormulaAggOp(node.op)) return shapeless(problems, id, `works out "${String(node.op)}"`);
        if (node.lines === '') return shapeless(problems, id, 'names no field to total over');
        if (node.op === 'count') {
          return node.of === undefined ? { kind: 'agg', op: 'count', lines: node.lines } : shapeless(problems, id, 'counts and reads a field too');
        }
        if (node.of === undefined || node.of === '') return shapeless(problems, id, 'names no field to read on each row');
        return { kind: 'agg', op: node.op, lines: node.lines, of: node.of };
      }

      default:
        return shapeless(problems, id, 'is a kind of piece this version does not know');
    }
  };

  const expr = byId.has(wire.root) ? build(wire.root, new Set(), 1) : null;

  for (const node of byId.values()) {
    if (reached.has(node.id)) continue;
    problems.push(
      problem('FORMULA_NODE_UNUSED', `"${node.id}" is given but nothing reads it. Take it out, or name it somewhere.`),
    );
  }

  if (problems.length > 0 || expr === null) return { ok: false, problems };
  return { ok: true, expr };
};

const shapeless = (problems: FormulaProblem[], id: string, what: string): null => {
  problems.push(problem('FORMULA_SHAPE', `"${id}" ${what}.`));
  return null;
};

/* --------------------------------------------------------------- the inverse */

/**
 * A tree as the flat list, for the scripted model, a proposal going back out,
 * and the round-trip test.
 *
 * Ids are `n1`, `n2`, ... in the order the walk meets the nodes, so a parent
 * always holds a lower number than its children. Children land in the list
 * before their parents; nothing depends on the order, and reading it bottom-up
 * is how a person checks one by hand.
 */
export const flattenFormula = (expr: FormulaNode): FormulaWire => {
  const nodes: FormulaWireNode[] = [];
  let counter = 0;

  const add = (node: FormulaNode): string => {
    const id = `n${(counter += 1)}`;
    switch (node.kind) {
      case 'field':
        nodes.push({ id, kind: 'field', fieldId: node.fieldId });
        return id;
      case 'hop':
        nodes.push({ id, kind: 'hop', via: node.via, fieldId: node.fieldId });
        return id;
      case 'const':
        nodes.push({ id, kind: 'const', value: node.value });
        return id;
      case 'calc': {
        const left = add(node.left);
        const right = add(node.right);
        nodes.push({ id, kind: 'calc', op: node.op, left, right });
        return id;
      }
      case 'round': {
        const of = add(node.of);
        nodes.push({ id, kind: 'round', of, decimals: node.decimals });
        return id;
      }
      case 'if': {
        const left = add(node.left);
        const right = node.right === undefined ? undefined : add(node.right);
        const then = add(node.then);
        const otherwise = add(node.otherwise);
        nodes.push({
          id,
          kind: 'if',
          left,
          op: node.op,
          ...(right === undefined ? {} : { right }),
          then,
          otherwise,
        });
        return id;
      }
      case 'days_between': {
        const from = add(node.from);
        const to = add(node.to);
        nodes.push({ id, kind: 'days_between', from, to });
        return id;
      }
      case 'concat': {
        const parts = node.parts.map(add);
        nodes.push({ id, kind: 'concat', parts });
        return id;
      }
      case 'coalesce': {
        const of = node.of.map(add);
        nodes.push({ id, kind: 'coalesce', of });
        return id;
      }
      case 'agg':
        nodes.push({ id, kind: 'agg', op: node.op, lines: node.lines, ...(node.of === undefined ? {} : { of: node.of }) });
        return id;
    }
  };

  const root = add(expr);
  return { root, nodes };
};
